import type { CurriculumModule } from "./curriculum-props";

/** Mirrors `course.phases[]` and the shape used in the lesson player sidebar. */
export type CoursePhaseInfo = {
  id: string;
  name: { en: string; bn?: string };
  moduleIds: string[];
  prerequisitePhaseId?: string;
  order: number;
};

/**
 * Returns phase ids that are still locked: the prerequisite phase has at least
 * one lesson that is not in `completedLessonIds`.
 */
export function computeLockedPhaseIds(
  phases: CoursePhaseInfo[],
  modules: CurriculumModule[],
  completedLessonIds: Set<string>,
): Set<string> {
  const locked = new Set<string>();

  const moduleLessons = new Map<string, string[]>(
    modules.map((m) => [m._id, m.lessons.map((l) => l._id)]),
  );

  function getPhaseLessonIds(phase: CoursePhaseInfo): string[] {
    return phase.moduleIds.flatMap((mid) => moduleLessons.get(mid) ?? []);
  }

  for (const phase of phases) {
    if (!phase.prerequisitePhaseId) continue;
    const prereq = phases.find((p) => p.id === phase.prerequisitePhaseId);
    if (!prereq) continue;
    const prereqLessons = getPhaseLessonIds(prereq);
    const allComplete =
      prereqLessons.length > 0 &&
      prereqLessons.every((id) => completedLessonIds.has(id));
    if (!allComplete) {
      locked.add(phase.id);
    }
  }

  return locked;
}

/**
 * module._id → phase (when the module is listed in that phase’s `moduleIds`).
 */
export function buildModuleToPhaseMap(phases: CoursePhaseInfo[]): Map<string, CoursePhaseInfo> {
  const map = new Map<string, CoursePhaseInfo>();
  const sorted = phases.slice().sort((a, b) => a.order - b.order);
  for (const phase of sorted) {
    for (const mid of phase.moduleIds) {
      map.set(mid, phase);
    }
  }
  return map;
}

/**
 * module._id → phase id (convenience for prev/next nav in the lesson player).
 */
export function buildModuleIdToPhaseIdMap(
  phases: Array<{ id: string; moduleIds: string[] }>,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const phase of phases) {
    for (const mid of phase.moduleIds) {
      m.set(mid, phase.id);
    }
  }
  return m;
}
